import { collector } from "../collector";
import { sanitizeUrl } from "../redact";

// Light, low-false-positive tamper/injection detection. LOG ONLY — never blocks
// the KYC flow. Signals: network-global tamper (vs the index.html sentinel),
// injected <script>/<iframe> after load, CSP violations, automation hints.
export function startIntegrity() {
  const orig = window.__diagOriginals || {};

  // Run after our own network wrapper is installed (so we don't flag ourselves).
  const tamperTimer = setTimeout(() => {
    const tampered = {};
    // window.fetch is expected to be OUR wrapper (__diagWrapped). Anything else
    // replacing it — or wrapping our wrapper — is a foreign patch.
    if (orig.fetch && window.fetch !== orig.fetch && !window.fetch.__diagWrapped) tampered.fetch = true;
    if (orig.XMLHttpRequest && window.XMLHttpRequest !== orig.XMLHttpRequest) tampered.XMLHttpRequest = true;
    if (orig.xhrSend && XMLHttpRequest.prototype.send !== orig.xhrSend) tampered.xhrSend = true;
    if (orig.xhrOpen && XMLHttpRequest.prototype.open !== orig.xhrOpen) tampered.xhrOpen = true;
    if (orig.sendBeacon && navigator.sendBeacon !== orig.sendBeacon) tampered.sendBeacon = true;
    if (orig.formSubmit && HTMLFormElement.prototype.submit !== orig.formSubmit) tampered.formSubmit = true;
    if (Object.keys(tampered).length) collector.track("tamper_globals", tampered, { critical: true });
  }, 3000);

  // Injected <script>/<iframe>/<object>/<embed> after initial load.
  let observer = null;
  try {
    observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!node || node.nodeType !== 1) continue;
          const tag = node.tagName;
          if (tag === "SCRIPT" || tag === "IFRAME" || tag === "OBJECT" || tag === "EMBED") {
            const src = node.src || (node.getAttribute && node.getAttribute("src")) || null;
            // First-party same-origin resources (e.g. CRA/webpack lazy chunks) are
            // the app's own code, not injection — skip them. Inline scripts (no src)
            // and cross-origin/extension/data:/blob: sources are still flagged.
            if (src) {
              let sameOrigin = false;
              try {
                sameOrigin = new URL(src, window.location.href).origin === window.location.origin;
              } catch (e) {
                /* unparseable src — treat as suspicious, fall through to flag */
              }
              if (sameOrigin) continue;
            }
            const extension = typeof src === "string" && src.indexOf("chrome-extension") === 0;
            collector.track(
              "dom_injection",
              { tag: tag.toLowerCase(), src: src ? sanitizeUrl(src) : null, extension, id: node.id || null },
              { critical: true }
            );
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {
    /* MutationObserver unavailable */
  }

  // CSP violations (fires for report-only and enforced policies, if a policy is set).
  const onCsp = (e) => {
    collector.track("csp_violation", {
      blockedUri: e.blockedURI,
      violatedDirective: e.violatedDirective || e.effectiveDirective,
      sourceFile: e.sourceFile ? sanitizeUrl(e.sourceFile) : null,
    });
  };
  document.addEventListener("securitypolicyviolation", onCsp);

  // Automation hint (advisory only, never a block).
  try {
    if (navigator.webdriver) collector.track("automation_hint", { webdriver: true });
  } catch (e) {
    /* noop */
  }

  return () => {
    clearTimeout(tamperTimer);
    if (observer) observer.disconnect();
    document.removeEventListener("securitypolicyviolation", onCsp);
  };
}
